import React from "react";

function Prompting101Content() {
  return (
    <div>
      <h2>Prompting 101</h2>
      <p>
        Effective prompting is key to getting the most out of Stackform. Because
        Stackform uses a multi-agent system, a well-crafted prompt helps the
        Orchestrator decide whether to answer directly, which agent to call, and
        how detailed the response should be. Here are some tips:
      </p>
      <ul>
        <li>
          <strong>Be Clear and Specific: </strong>
          The more specific your request, the better the Orchestrator can
          understand your intent and route it correctly. Instead of "tell me
          about AI," try "explain how large language models are trained,
          focusing on transformer architecture."
        </li>
        <li>
          <strong>Provide Context and Constraints: </strong>
          Context helps the system tailor the response to your level and
          needs. For example: "I'm a beginner data scientist — explain
          gradient descent with a simple analogy."
        </li>
        <li>
          <strong>Direct the Agent When You Know What You Need: </strong>
          If you know which agent is best for your query, say so explicitly.
          This bypasses the Orchestrator's routing decision and goes straight
          to the specialist.
          <ul>
            <li><em>"Use the researcher agent to find papers on climate change mitigation."</em></li>
            <li><em>"Use the generalist agent to search for the latest iPhone release."</em></li>
          </ul>
        </li>
        <li>
          <strong>Control Speed vs. Depth: </strong>
          Tell Stackform how much depth you want. For instant answers, use
          keywords like <em>"quick answer"</em>, <em>"no agent"</em>, or{" "}
          <em>"fast response"</em> — the Orchestrator will answer directly
          without calling a sub-agent. For the deepest analysis, use{" "}
          <strong>Expansive Mode</strong> on a previous response.
        </li>
        <li>
          <strong>Use Saved Personas: </strong>
          Stackform supports <strong>Personas</strong> — saved system prompts
          that give any agent a persistent role, tone, or expertise for the
          entire session. Go to <strong>User Settings → Personas</strong> to
          create one (e.g., "financial analyst," "devil's advocate," "plain
          English editor"), then assign it to an agent under{" "}
          <strong>User Settings → Model Defaults → Agent Models</strong>. You
          can also describe a role inline in any prompt: "Explain this as if
          you were teaching a high school student."
        </li>
        <li>
          <strong>Break Down Complex Tasks: </strong>
          For multi-part problems, ask one focused question at a time. This
          helps each agent deliver a thorough answer rather than a broad
          overview. You can chain follow-up questions in the same conversation
          — Stackform remembers your history.
        </li>
        <li>
          <strong>Build a Workflow for repeatable pipelines: </strong>
          For multi-step tasks you run often, use the{" "}
          <strong>Workflow Builder</strong> to chain agents and tools into a
          reusable pipeline (for example, web search → research → evaluation)
          and run it with one click. See <strong>Workflows</strong> in the help
          menu.
        </li>
        <li>
          <strong>Use Few-Shot Examples: </strong>
          Providing an example of the output you want can dramatically improve
          results. For instance: "Translate these phrases to French:
          'hello' → 'bonjour', 'goodbye' → 'au revoir'. Now translate
          'good morning'."
        </li>
        <li>
          <strong>Ask for an Evaluation: </strong>
          Not sure if an answer is complete or accurate? Use{" "}
          <strong>Evaluation Mode</strong> on any message to have a dedicated
          evaluator agent critically review the response and give you a
          structured quality report.
        </li>
      </ul>
    </div>
  );
}

export default Prompting101Content;
